import { type ReactNode, useContext } from 'react'
import { Pressable, StyleSheet } from 'react-native'
import Animated from 'react-native-reanimated'

import {
  ACTION_PRESSED_DIM,
  ACTION_PRESSED_SCALE,
  usePressScale,
} from '../hooks/usePressScale'
import { DEPOSIT_METRICS } from '../screens/depositMetrics'
import { DepositActionLayers, DepositActionMotion } from './DepositActionMotion'

import { Box, Text, useTheme } from '../theme/restyle'
import { cornerCurve } from '../theme/cornerCurve'
import type { FunkitTheme } from '../theme/toRestyleTheme'

export type PillButtonSize = Extract<
  keyof FunkitTheme['sizes'],
  `button${string}`
>

export type PillButtonVariant = 'primary' | 'secondary'

export interface PillButtonProps {
  label: string
  onPress: () => void
  /** Leading content inside the pill, e.g. a 16px icon. */
  icon?: ReactNode
  /** Content pinned without shifting the centered label. */
  trailingIcon?: ReactNode
  size?: PillButtonSize
  /** `secondary` is the quieter fill used for side-by-side actions. */
  variant?: PillButtonVariant
  /** `element` squares the corners off where the design butts the button
   *  against a card of the same radius. */
  radius?: Extract<keyof FunkitTheme['borderRadii'], 'button' | 'element'>
  disabled?: boolean
  /** Defaults to `label`. */
  accessibilityLabel?: string
}

/**
 * The full-width primary action at the foot of a sheet — Confirm, Cancel, Done.
 * Width comes from the container, so callers control the horizontal inset.
 */
export function PillButton({
  label,
  onPress,
  icon,
  trailingIcon,
  size = 'buttonLg',
  variant = 'primary',
  radius = 'button',
  disabled = false,
  accessibilityLabel,
}: PillButtonProps) {
  const animateDeposit = useContext(DepositActionMotion)
  // The give lives inside the `Pressable`, so the touch target keeps its full
  // size while the pill dips: a finger near the edge cannot slide off the
  // control it is already pressing. Deeper than a row's, and with a tap, because
  // this is the press a buyer needs to feel they have made.
  const { pressStyle, onPressIn, onPressOut } = usePressScale({
    scale: ACTION_PRESSED_SCALE,
    dim: ACTION_PRESSED_DIM,
    haptics: true,
  })
  const theme = useTheme()
  const surface = theme.background.button[variant]
  const labelColor = theme.content.button[variant].default
  const dim = disabled ? theme.opacity.disabled / 100 : 1
  const height = theme.sizes[size]
  // A brand that squares its actions off (Pump's 12pt) gets the design's 60%
  // smoothing; the token's own pill keeps its round ends.
  const borderCurve = cornerCurve(theme.borderRadii[radius], height)

  if (animateDeposit) {
    return (
      <Pressable
        onPress={onPress}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? label}
        accessibilityState={{ disabled }}
      >
        <Animated.View style={pressStyle}>
          <DepositActionLayers
            stateKey={`${label}:${variant}:${!!trailingIcon}`}
            style={{ height }}
            fillColor={surface.default}
            radius={theme.borderRadii[radius]}
            borderCurve={borderCurve}
            backdropColor={theme.background.modal}
            fillOpacity={dim}
            contentOpacity={dim}
            contentStyle={[
              styles.pill,
              styles.animatedContent,
              variant === 'secondary' ? styles.secondaryPill : null,
            ]}
          >
            {icon}
            <Text
              variant="bodyMedium"
              numberOfLines={1}
              style={{
                color: labelColor,
                fontSize: DEPOSIT_METRICS.button.fontSize,
                letterSpacing: DEPOSIT_METRICS.button.tracking,
              }}
            >
              {label}
            </Text>
            {trailingIcon ? (
              <Box style={styles.trailing}>{trailingIcon}</Box>
            ) : null}
          </DepositActionLayers>
        </Animated.View>
      </Pressable>
    )
  }

  return (
    <Pressable
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityState={{ disabled }}
    >
      {/* Disabled dims the fill rather than recoloring it (the token export has
          no disabled button color), so an opaque backing keeps whatever sits
          behind — a scrolling list — from reading through the button. */}
      <Animated.View style={pressStyle}>
        <Box
          borderRadius={radius}
          style={{ backgroundColor: theme.background.modal, borderCurve }}
        >
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            borderRadius={radius}
            style={[
              styles.pill,
              variant === 'secondary' ? styles.secondaryPill : null,
              {
                height,
                backgroundColor: surface.default,
                borderCurve,
                opacity: dim,
              },
            ]}
          >
            {icon}
            <Text
              variant="bodyMedium"
              numberOfLines={1}
              style={{ color: labelColor }}
            >
              {label}
            </Text>
            {trailingIcon ? (
              <Box style={styles.trailing}>{trailingIcon}</Box>
            ) : null}
          </Box>
        </Box>
      </Animated.View>
    </Pressable>
  )
}

const styles = StyleSheet.create({
  // Absolute so the label stays optically centred in the pill.
  trailing: { position: 'absolute', right: 20 },
  pill: { paddingHorizontal: 24, gap: 8 },
  animatedContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // The design's own inset for these (`px-10` vs the primary's `px-24`): they
  // sit several-across, where 24 leaves no room for the label and it ellipsises.
  secondaryPill: { paddingHorizontal: 10 },
})
